import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { urlSearchParams } from 'srcReact/helpers/utils'
import PageContainer from 'srcReact/PageContainer/PageContainer'
import SearchTextBox from 'srcReact/components/common2/SearchTextBox'
import styles from './styles.module.scss'
import { checkNested } from 'srcReact/helpers/validator'
import BackNavigation from 'srcReact/components/common2/BackNavigation'
import FilterMenu from 'srcReact/components/common2/FilterMenu'
import { globalSearchActions } from 'srcReact/SearchPage'
import Spinner from 'srcReact/components/common2/Spinner'
import All from 'srcReact/SearchPage/components/All'
import Groups from 'srcReact/SearchPage/components/Groups'
import Mixrs from 'srcReact/SearchPage/components/Mixrs'
import Members from 'srcReact/SearchPage/components/Members'
import Media from 'srcReact/SearchPage/components/Media'
import Posts from 'srcReact/SearchPage/components/Posts'
import useInfinitScroll from 'srcReact/helpers/hooks/useInfinitScroll'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import GlobalSearchSuggestionPopin from 'srcReact/components/popinsContent/GlobalSearchSuggestionPopin'

const SearchPageContainer = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    common: { isNative, isMobile, userLocation },
    globalSearch: {
      searchText,
      searchInterestText,
      loading,
      results,
      loaded,
      stat,
      filters,
      filterItem,
      searchSuggestionList
    },
    organization: { current_organization }
  } = useSelector((state) => state)
  const [isBackNavigation] = useState(true)
  const [showSearchBar, setShowSearchBar] = useState(false)
  const [searchValue, setSearchValue] = useState(searchText)
  const [announcement, setAnnouncement] = useState('')
  const [pagination, setPagination] = useState({
    nextOffset: 0,
    isPagination: false
  })

  useEffect(() => {
    if (searchText) {
      setSearchValue(searchText)
    }
  }, [searchText])

  useEffect(() => {
    if (!loading && loaded && results?.length > 0) {
      setAnnouncement(t('globalSearch.accessibility.result', { text: searchText }))
    }
  }, [loading, loaded, results])
  useEffect(() => {
    if (isNative && searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH) {
      dispatch(
        globalSearchActions.globalSearch(
          urlSearchParams('organization_id'),
          searchText,
          urlSearchParams('lat'),
          urlSearchParams('lon'),
          filterItem
        )
      )
      return
    }
    if (
      checkNested(current_organization, 'info', 'id') &&
      searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH &&
      userLocation
    ) {
      dispatch(
        globalSearchActions.globalSearch(
          current_organization.info.id,
          searchText,
          userLocation.latitude,
          userLocation.longitude,
          filterItem
        )
      )
    }
  }, [searchText, filterItem])
  useEffect(() => {
    if (isNative && searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH && pagination.isPagination) {
      dispatch(
        globalSearchActions.globalSearchPagination(
          urlSearchParams('organization_id'),
          searchText,
          urlSearchParams('lat'),
          urlSearchParams('lon'),
          filterItem,
          pagination.nextOffset
        )
      )
      return
    }
    if (
      checkNested(current_organization, 'info', 'id') &&
      searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH &&
      pagination.isPagination &&
      userLocation
    ) {
      dispatch(
        globalSearchActions.globalSearchPagination(
          current_organization.info.id,
          searchText,
          userLocation.latitude,
          userLocation.longitude,
          filterItem,
          pagination.nextOffset
        )
      )
    }
  }, [pagination])

  const infiniteScrollRef = useInfinitScroll(loading, stat.remaining, stat.nextOffset, setPagination, [loading])
  const handleSearch = (value) => {
    dispatch(globalSearchActions.setSearchText(value))
  }
  const handleSuggestionSearch = (value) => {
    if (value.length === 0) return
    dispatch(globalSearchActions.setSearchText(value))
    setShowSearchBar(false)
    if (showSearchBar) document.getElementById('searchInput').blur()
  }
  const setFilterItem = (filter) => {
    dispatch(globalSearchActions.setFilterItem(filter))
  }
  const renderSearchContent = () => {
    const commonProps = {
      isMobile,
      isNative,
      infiniteScrollRef,
      isBackNavigation
    }
    switch (filterItem) {
      case 'all':
        return <All {...commonProps} />
      case 'mixrs':
      case 'my_mixrs':
        return <Mixrs {...commonProps} />
      case 'groups':
      case 'my_groups':
        return <Groups {...commonProps} />
      case 'members':
        return <Members {...commonProps} />
      case 'media':
        return <Media {...commonProps} />
      case 'posts':
        return <Posts {...commonProps} />
    }
  }
  const isSearchFoucs = isMobile && showSearchBar
  const handleShowHideSearchBar = () => {
    setShowSearchBar(!showSearchBar)
  }
  const handleSearchFocus = () => {
    if (!isSearchFoucs) {
      setSearchValue(searchText)
    }
    setShowSearchBar(true)
  }

  return (
    <PageContainer
      pageTitle={t('globalSearch.title')}
      withFooter={false}
      withSidebar={true}
      pageClassName={styles.searchPageContainer}>
      <section className={styles.searchPageWrapper} aria-label={t('globalSearch.accessibility.resultSection')}>
        {!isSearchFoucs && <BackNavigation backText={t('globalSearch.exit_search')} />}
        {loading && (
          <div aria-live='assertive' aria-atomic='true' style={{ position: 'absolute', left: '-9999px' }}>
            {t('globalSearch.accessibility.searchInProgress', { text: searchText })}
          </div>
        )}
        <div aria-live='polite' aria-atomic='true' style={{ position: 'absolute', left: '-9999px' }}>
          {announcement}
        </div>
        {!isMobile ? (
          <SearchTextBox
            value={searchText}
            {...(searchText.length === 0 && { clear: true })}
            minimumSearchChar={1}
            size='large'
            iconPosition='start'
            placeholder={t('globalSearch.searchPlaceholder')}
            handleSearch={handleSearch}
            cssClass={styles.searchInputContainer}
            inputCssClass={styles.searchInput}
            ariaLabel={t('globalSearch.accessibility.searchDescription')}
          />
        ) : (
          <SearchTextBox
            value={isSearchFoucs ? searchValue : searchText}
            {...((isSearchFoucs ? searchValue : searchText).length === 0 && { clear: true })}
            minimumSearchChar={3}
            size='large'
            type={isSearchFoucs ? 'search' : 'text'}
            icon={isSearchFoucs ? 'arrow-left24' : 'search24'}
            iconPosition='start'
            placeholder={t('globalSearch.searchPlaceholder')}
            handleSearch={(value) => (isSearchFoucs ? setSearchValue(value) : handleSearch(value))}
            handleIconClick={handleShowHideSearchBar}
            cssClass={styles.searchInputContainer}
            inputCssClass={`${styles.searchInput} ${isSearchFoucs && styles.closeIcon}`}
            handleFocus={handleSearchFocus}
            handleKeyDown={handleSuggestionSearch}
            id='searchInput'
            ariaLabel={t('globalSearch.accessibility.searchDescription')}
          />
        )}

        {((isSearchFoucs && searchSuggestionList.length >= 1) ||
          (!isMobile &&
            searchText.length < appConstantSettings.GLOBAL_SEARCH_LENGTH &&
            searchSuggestionList.length >= 1)) && <GlobalSearchSuggestionPopin handleSearch={handleSuggestionSearch} />}
        {!isSearchFoucs && searchText.length >= appConstantSettings.GLOBAL_SEARCH_LENGTH && (
          <React.Fragment>
            {searchText?.length > 1 && <div className={styles.searchText}>{`“${searchText}”`}</div>}
            <FilterMenu
              filterItems={t('globalSearch.filterItems', { returnObjects: true })}
              filterItem={filterItem}
              setFilterItem={setFilterItem}
              filterStats={filters}
              filterClassName={styles.filterClassName}
              buttonClassName={styles.buttonClassName}
              hasTabCount={true}
            />
            {loaded && (
              <div className={`${styles.searchContent} ${isNative && styles.isNative}`}>
                <div className={styles.totalResults}>{`${stat.total} ${t('globalSearch.results')}`}</div>
                {renderSearchContent()}
                {loading && loaded && <Spinner />}
              </div>
            )}
            {loading && !loaded && <Spinner />}
          </React.Fragment>
        )}
      </section>
    </PageContainer>
  )
}

export { SearchPageContainer }
